/**
 * Composition de la landing en fenêtre ordinateur dont la page serait coupée : un seul endroit pour les
 * constantes de calibrage, la porte `@media`, les options de la jumelle (`TWIN_OPTIONS`), la feuille
 * écrite à la main `compositionCss` et la fabrique `cadreTwin`.
 *
 * Porte unique par paliers de largeur (`PAGE_STEPS` -> `PAGE_QUERY`) : elle suit la frontière de coupe
 * mesurée sans format cadré, de 941 px de large à l'infini. Hors porte, aucune déclaration nouvelle (les deux
 * `@property` du préambule ne sont lues par aucune règle hors porte).
 * Largeur de composition : `cw = min(vw, CW_SLOPE * (vh - CW_RESERVE_H))`.
 *
 * `compositionCss` est inséré dans `authoredCss` (`./authored`) AVANT `mobileCss` : la garde de la
 * feuille mobile reste S8 (`styles/mobile.test.ts`, `authoredCss.endsWith(mobileCss)`).
 *
 * Préambule hors de tout `@media` : `FIG_PROPS`, deux `@property` (`--fig-col` `<length>`, `--fig-s` `<number>`). Puis un
 * seul bloc sous `PAGE_QUERY`, familles dans cet ordre :
 * - V : variables `--cw` (largeur de composition), `--cvw` (1 % de `--cw`), `--cpx` (1 px mis à l'échelle) ;
 * - I : jumelles des styles en ligne du cockpit (`scenes/cockpit.tsx`) ;
 * - H : jumelles des marges hors-champ en `3vw` (`hero.css`, `cockpit.css`, `header.css`), sans ce terme ;
 * - B : planches centrées sous la bande de dégradé de l'en-tête (`.pin`, `FADE_BAND_PX`, `PIN_BALANCE_PX`) ;
 * - X : cadre `.cframe` 16/10, conteneur de la figure 01, sans fond ni ombre ;
 * - F : figures 01, 05, 06, 07 dessinées à leur largeur d'ordinateur (`FIG_DESIGN_W`) dans une fenêtre 16/10, réduites
 *   d'un bloc par `--fig-s = tan(atan2(colonne, largeur de dessin))` (règle du téléphone, `styles/mobile.ts` D1a-D8) ;
 * - N : crans tablette 941-1150 px (`hero.css`, `risques.css`) ramenés aux valeurs d'ordinateur dans les figures.
 *
 * Contraintes d'écriture de `compositionCss` (reprises de `styles/mobile.ts`) : toute déclaration du bloc `PAGE_QUERY`
 * est `!important` (propriétés personnalisées comprises), aucun commentaire CSS dans la chaîne, pas de backtick, pas de
 * `color-mix()` ni de `@font-face` ; aucun `vw` hors du `100vw` de `--cw`, et `cqw` seulement dans `--fig-col`.
 *
 * `cadreTwin` : jumelle en importance normale, à injecter juste après ses feuilles (le `!important`
 * n'est pas nécessaire : même ordre de cascade que la source).
 */
import { scaleLengths, twinSheet, type LengthOptions, type TwinOptions } from "./twin-sheet";

// ── Constantes de calibrage ──

/**
 * Pente : `cw = min(vw, CW_SLOPE * (vh - CW_RESERVE_H))`. Réciproque de la pente de la frontière de coupe
 * mesurée sans format cadré (planche risques, 1241-1515 px de large : hauteur minimale ~ 0,4778 * L + 107),
 * soit 1 / 0,4778. Mesurée au banc ; recalage : README « Recalage ».
 */
export const CW_SLOPE = 2.09;
/**
 * Réserve de hauteur : `max(P0(L) − L / CW_SLOPE)` = 108,0 (1256 px, planche risques), plus 2 × 5 px de marge visée.
 * `P0(L)` = plus petite hauteur où la page tient, porte ouverte partout avec `--cw = 100vw` et figures en format cadré.
 * Absorbe l'en-tête fixe. Mesurée au banc ; recalage : README « Recalage ».
 */
export const CW_RESERVE_H = 118;
/**
 * Porte unique par paliers de largeur `[largeur de départ, hauteur max]` : composition de `fromWidth` à la
 * largeur de départ suivante - 0,02 px (aucun trou aux largeurs fractionnaires, convention de
 * `MOBILE_QUERY`), jusqu'à `maxHeight` ; dernier palier sans borne de largeur. Dérivée de la frontière de
 * coupe `F0(L)` (plus petite hauteur où toutes les planches tiennent sans format cadré ; « tient » = haut réel des
 * glyphes et des cadres ≥ hdrh + FADE_BAND_PX − 5, tolérance de 5 px au bas de la bande de dégradé (A1 1b), et
 * bas ≤ H) :
 * `maxHeight >= max F0 - 1` (toute fenêtre coupée entre), `<= min F0 + 19` (aucune fenêtre qui tient avec
 * 10 px ou plus), cible `min(max F0 + 7, min F0 + 19)`. À recaler au banc
 * après toute retouche de texte ou de feuille, avec la fixture de frontière de `composition.test.ts`
 * (README « Recalage »).
 */
export const PAGE_STEPS: readonly (readonly [fromWidth: number, maxHeight: number])[] = [
	[941, 856], // 941-949 : cockpit (titre docké), F0 849
	[950, 838], // 950-961 : cockpit (titre docké), F0 831
	[962, 822], // 962-1029 : cockpit (titre docké), F0 811-815
	[1030, 804], // 1030-1093 : cockpit (titre docké), F0 797
	[1094, 790], // 1094-1191 : cockpit (titre docké), F0 780-783
	[1192, 772], // 1192-1225 : cockpit (titre docké), F0 763-765
	[1226, 764], // 1226-1346 : cockpit (titre docké) / risques, F0 745-765
	[1347, 784], // 1347-1390 : risques, F0 766-786
	[1391, 805], // 1391-1433 : risques, F0 786-806
	[1434, 825], // 1434-1479 : risques, F0 806-826
	[1480, 845], // 1480-1515 : risques, F0 827-844
	[1516, 841], // 1516-1571 : carte, F0 822-842
	[1572, 860], // 1572-1815 : carte, F0 843-853
	[1816, 855], // 1816-1858 : cockpit (titre docké), F0 836-856
	[1859, 875], // 1859-1906 : cockpit (titre docké), F0 856-876
	[1907, 895], // 1907-1960 : cockpit (titre docké), F0 877-896
	[1961, 915], // 1961-2007 : cockpit (titre docké), F0 897-917
	[2008, 936], // 2008-2064 : cockpit (titre docké), F0 917-937
	[2065, 956], // 2065-2125 : cockpit (titre docké), F0 938-958
	[2126, 976], // 2126-2189 : cockpit (titre docké), F0 958-978
	[2190, 997], // 2190-2243 : cockpit (titre docké), F0 978-998
	[2244, 1017], // 2244-2304 : cockpit (titre docké), F0 998-1018
	[2305, 1037], // 2305-2365 : cockpit (titre docké), F0 1019-1038
	[2366, 1057], // 2366-2424 : cockpit (titre docké), F0 1039-1059
	[2425, 1078], // 2425 et plus : cockpit (titre docké), F0 1059-1077
];
/** Marge visée sous la bande de dégradé de l'en-tête, en px (famille B). Calibrage mesuré. */
export const PIN_BALANCE_PX = 5;
/**
 * Largeurs de dessin des quatre fenêtres (px), mesurées à 1728×1117 : largeurs visibles des quatre fenêtres en
 * plein écran, figure 01 sans le débord `3vw` ; à remesurer après toute retouche de `hero.css`, `cockpit.css`,
 * `risques.css`, `assistant.css` ou `carte.css` (README « Recalage »).
 */
export const FIG_DESIGN_W = { cockpit: 971.38, risques: 990.69, assistant: 1060.86, carte: 1053.95 } as const;
/**
 * Largeur de composition où `.step p` atteint son plancher 10,5 px : 10,5 / (0,906 / 100)
 * (`commons.css:46`). Dérivée, gardée par le test Q10.
 */
export const BLOC_FROM_CW = 10.5 / 0.00906;

// ── Seuils et longueurs recopiés (gardés par test) ──

/** Largeur min du desktop (`commons.css:42`). */
export const DESKTOP_MIN_W = 941;
/** Hauteur de la bande de dégradé sous l'en-tête en px (`header.css:17`, `.hdr::before{bottom:-20px}`). */
export const FADE_BAND_PX = 20;
/** Ratio des fenêtres de figure (`hero.css:55`, `aspect-ratio:16/10`) ; même valeur que `DEMO_RATIO` (`styles/mobile.ts:80`). */
export const FIG_RATIO = [16, 10] as const;
/** Fenêtre de référence des largeurs de dessin : plein écran du 16" (`description.md:8`). */
export const FIG_REF_W = 1728;
/** Corps de texte en px (`base.css:87`). */
export const BODY_FONT_PX = 15.5;
/** Variables de `:root` rejouées dans les figures dessinées à leur taille d'ordinateur (`base.css:63,22,78,79`). */
export const FIG_ROOT_VARS: readonly string[] = [
	"--r:12px",
	"--sh-card:0 25px 50px -12px rgba(0,0,0,.12)",
	"--sh-frame:0 0 0 1px var(--line-2),0 1px 2px rgba(22,20,15,.04),0 12px 28px -14px rgba(22,20,15,.14),0 32px 64px -36px rgba(22,20,15,.20)",
	"--sh-pop:0 0 0 1px var(--line-2),0 2px 6px rgba(22,20,15,.06),0 16px 40px -20px rgba(22,20,15,.22)",
];
/** `clamp(min px, vw, max px)` du titre de l'acte (`scenes/cockpit.tsx:123`). */
export const ACTE_TITLE_CLAMP = [28, 2.8, 48] as const;
/** Marge haute des étapes de l'acte en px (`scenes/cockpit.tsx:126`). */
export const ACTE_STEPS_MT_PX = 26;
/** Marge latérale de la page en px (`commons.css:15`, `hero.css:52`, `header.css:94`). */
export const PAGE_PAD_PX = 16;
/** Padding droit de `.ck-head` en px (`cockpit.css:6`). */
export const CK_HEAD_PAD_R_PX = 20;
/** Padding droit de `.ck-body` en px (`cockpit.css:27`). */
export const CK_BODY_PAD_R_PX = 16;

export const CW_VAR = "--cw";
export const CVW_VAR = "--cvw";
export const CPX_VAR = "--cpx";
/** Classe de l'enveloppe de réduction de la figure 01 (`scenes/cockpit.tsx`, règle F1). */
export const FIT_CLASS = "ck-fit";
/** Largeur de colonne disponible pour une figure (`cqw`). */
export const FIG_COL_VAR = "--fig-col";
/** Facteur de réduction d'une figure. */
export const FIG_S_VAR = "--fig-s";

/** Préambule hors `@media` : types des variables de réduction (descripteurs `@property`, donc sans `!important`). */
export const FIG_PROPS = `@property ${FIG_COL_VAR}{syntax:'<length>';inherits:true;initial-value:0px}\n@property ${FIG_S_VAR}{syntax:'<number>';inherits:true;initial-value:1}`;

// ── Aides locales ──

/** Recopie de `styles/mobile.ts` : règle dont chaque déclaration est `!important`. */
const rule = (sel: string, decls: readonly string[]): string =>
	`${sel}{${decls.map((d) => `${d}!important`).join(";")}}`;

/** Arrondi à 2 décimales sans zéros inutiles (`BLOC_FROM_CW` -> "1158.94"). */
const fmt2 = (n: number): string => String(Number(n.toFixed(2)));

const PX = `var(${CPX_VAR})`;
const VW = `var(${CVW_VAR})`;
/** Repli = hauteur réelle de l'en-tête (`header.css:59`), comme `styles/mobile.ts`. */
const HDRH = "var(--hdrh,60px)";

const LEN: LengthOptions = { px: PX, vw: VW, hairline: 1 };

/** Convertit les longueurs de la valeur d'une déclaration `prop:valeur`. */
const scaleDecl = (d: string, o: LengthOptions): string => {
	const i = d.indexOf(":");
	return `${d.slice(0, i)}:${scaleLengths(d.slice(i + 1), o)}`;
};

// ── Porte ──

/** Alternatives de `PAGE_STEPS` jointes par `", "`. */
const stepsQuery = (): string =>
	PAGE_STEPS.map(([from, h], i) => {
		const next = PAGE_STEPS[i + 1];
		const upTo = next === undefined ? "" : ` and (max-width: ${fmt2(next[0] - 0.02)}px)`;
		return `(min-width: ${from}px)${upTo} and (max-height: ${h}px)`;
	}).join(", ");

export const PAGE_QUERY = stepsQuery();

/** Options de la jumelle ; chaque exception est gardée par les tests Q14. */
export const TWIN_OPTIONS: TwinOptions = {
	px: PX,
	vw: VW,
	hairline: 1,
	// En-tête et pied rejoués sans conversion (`header.css:16-87`, `cta-footer.css:43-89`).
	chrome: /^(?:\.hdr|\.logo|\.nav|\.hbtn|footer)(?![\w-])/,
	keep: [
		// `cockpit.css:131` <-> `scenes/cockpit.anim.ts:198` (`mt * 10` px).
		[".maps-track", "gap"],
		// `coulisses.css:58` <-> `scenes/coulisses-figures.ts:456` (`TOP=110`).
		[".fig1-ov", "top"],
		[".fig1-ov", "height"],
		// `coulisses.css:27` <-> `scenes/cockpit.anim.ts:309,326` (`+ 16`).
		[".viscap", "margin-top"],
		// `coulisses.css:93` : unités utilisateur d'un SVG à `viewBox`, sinon double mise à l'échelle.
		[".figrow text", "font-size"],
		// `base.css:94` : © du pied sur la verticale du logo à 16 px (`cta-footer.css:44-50`, `header.css:64`).
		[".wrap", "padding"],
	],
	// `carte.css:28-30` : `color-mix()` imbriqué dans `linear-gradient()`, que `css-tree` ne sait pas
	// analyser (`styles/raw-import.test.ts`) ; texture décorative à `opacity:.75`.
	omit: [[".ce-bm", "background"]],
	// `cta-footer.css:36,89` : les blocs mobiles (max-width < desktop) ne sont jamais rejoués.
	dropMedia: (p) => {
		const m = /^\(\s*max-width\s*:\s*(\d+(?:\.\d+)?)px\s*\)$/.exec(p.trim());
		return m !== null && Number(m[1]) < DESKTOP_MIN_W;
	},
};

// ── Familles V, I ──

const V = rule(":root", [
	`${CW_VAR}:min(100vw, (100vh - ${CW_RESERVE_H}px) * ${String(CW_SLOPE)})`,
	`${CVW_VAR}:calc(var(${CW_VAR}) / 100)`,
	`${CPX_VAR}:min(1px, var(${CW_VAR}) / ${fmt2(BLOC_FROM_CW)})`,
]);

const I = [
	rule("#acte-block>h2.sec", [
		scaleDecl(`font-size:clamp(${ACTE_TITLE_CLAMP[0]}px,${ACTE_TITLE_CLAMP[1]}vw,${ACTE_TITLE_CLAMP[2]}px)`, LEN),
	]),
	rule("#acte-block>.steps", [scaleDecl(`margin-top:${ACTE_STEPS_MT_PX}px`, LEN)]),
];

// ── Famille H : jumelles des marges hors-champ, sans le terme `3vw` (dans la porte, la fenêtre produit reste dans la composition) ──

const OUT = `max((var(${CW_VAR}) - var(--pagew)) / 2, 0px) + ${PAGE_PAD_PX} * ${PX}`;
const H = [
	rule(".cframe", [`margin-right:calc(-1 * (${OUT}))`]),
	rule(".ck-head", [`padding-right:calc(${CK_HEAD_PAD_R_PX} * ${PX})`]),
	rule(".ck-body", [`padding-right:calc(${CK_BODY_PAD_R_PX} * ${PX})`]),
	rule(".scroll-cue", [`transform:translateX(calc((${OUT}) / 2))`]),
];

// ── Familles B, X, F : figures dessinées à leur largeur d'ordinateur dans une fenêtre 16/10, réduites d'un bloc ──

/** Cartouche sous la figure 06 (`coulisses.css:27`, `.viscap{margin-top}`). */
const CAP_GAP_PX = 16;
/** Corps du texte de cartouche (`coulisses.css:11`, `.figtxt .fg`). */
const CAP_FONT_PX = 11.5;
/** Interligne du corps (`base.css:87`). */
const BODY_LH = 1.6;
/** Hors-page du PDF et rapport d'une page (`assistant.css:81`). */
const PDF_CHROME_PX = 192;
const PDF_PAGE_RATIO = 1.4143;
/** Marge gauche du PDF au maximum du `clamp` (`assistant.css:81`). */
const PDF_GAP_PX = 24;
/** Pistes et gap de la grille de l'assistant (`assistant.css:11`). */
const ASSISTANT_FR = [1.6, 0.58] as const;
const ASSISTANT_GAP = "clamp(20px,2.5vw,44px)";

/** ‹D› : environnement d'ordinateur rejoué dans une figure (échelle 1, `vw` de la fenêtre de référence, `:root`, corps). */
const DESIGN: readonly string[] = [
	`${CPX_VAR}:1px`,
	`${CVW_VAR}:${fmt2(FIG_REF_W / 100)}px`,
	...FIG_ROOT_VARS,
	`font-size:${BODY_FONT_PX}px`,
];

/** Hauteur de la fenêtre de dessin : largeur / ratio. */
const designH = (w: number): string => `${w}px / ${FIG_RATIO[0] / FIG_RATIO[1]}`;

/** Colonne disponible (`col`, `100cqw` par défaut) et facteur `tan(atan2(colonne, largeur de dessin))`. */
const factor = (w: number, col = "100cqw"): readonly string[] => [
	`${FIG_COL_VAR}:${col}`,
	`${FIG_S_VAR}:tan(atan2(var(${FIG_COL_VAR}), ${w}px))`,
];

/** Marge compensant l'espace libéré par la réduction (le `scale` ne change pas la boîte de mise en page). */
const comp = (w: number, bottom = `calc(${designH(w)} * (var(${FIG_S_VAR}) - 1))`): string =>
	`margin:0 calc(${w}px * (var(${FIG_S_VAR}) - 1)) ${bottom} 0`;

/** F3, F5 : fenêtre de dessin réduite par `scale`. */
const pane = (sel: string, w: number): string =>
	rule(sel, [
		`width:${w}px`,
		`height:calc(${designH(w)})`,
		"max-width:none",
		"flex:none",
		...factor(w),
		"transform-origin:0 0",
		`scale:var(${FIG_S_VAR})`,
		comp(w),
		"will-change:auto",
		...DESIGN,
	]);

/** F4, F6 : curseur de la même figure, mêmes variables de réduction. */
const cursor = (sel: string, w: number): string =>
	rule(sel, [...factor(w), `scale:var(${FIG_S_VAR})`, `${CPX_VAR}:1px`]);

const B = rule(".pin", [`padding-top:calc(${HDRH} + ${FADE_BAND_PX}px + ${PIN_BALANCE_PX} * ${PX})`]);

const X = rule(".cframe", [
	`aspect-ratio:${FIG_RATIO[0]}/${FIG_RATIO[1]}`,
	"max-height:none",
	"min-height:0",
	"container-type:inline-size",
	"overflow:visible",
	"background:transparent",
	"box-shadow:none",
]);

const ASSISTANT_SUM = fmt2(ASSISTANT_FR[0] + ASSISTANT_FR[1]);
const CAP_H = fmt2(CAP_FONT_PX * BODY_LH);
const SCALED = `scale(var(${FIG_S_VAR}))`;

const F: readonly string[] = [
	// F1 : figure 01 (cockpit), enveloppe `FIT_CLASS`
	rule(`#cframe>.${FIT_CLASS}`, [
		"display:flex",
		"flex-direction:column",
		"flex:none",
		`width:${FIG_DESIGN_W.cockpit}px`,
		`height:calc(${designH(FIG_DESIGN_W.cockpit)})`,
		...factor(FIG_DESIGN_W.cockpit),
		"transform-origin:0 0",
		`transform:${SCALED}`,
		"will-change:auto",
		...DESIGN,
		"background:var(--card)",
		"border-radius:var(--r)",
		"box-shadow:var(--sh-frame)",
		"overflow:hidden",
	]),
	// F2 : conteneurs des figures 05 et 07
	rule("#rq-stage,#ce-stage", ["container-type:inline-size"]),
	// F3-F4 : figure 05 (risques)
	pane("#rq-frame", FIG_DESIGN_W.risques),
	cursor("#rq-cur", FIG_DESIGN_W.risques),
	// F5-F6 : figure 07 (carte)
	pane(".ce-frame", FIG_DESIGN_W.carte),
	cursor("#ce-cur", FIG_DESIGN_W.carte),
	// F7-F14 : figure 06 (assistant)
	rule("#assistant .grid", [
		"container-type:inline-size",
		`grid-template-columns:${ASSISTANT_FR.map((t) => `minmax(0,${String(t).replace(/^0\./, ".")}fr)`).join(" ")}`,
	]),
	rule(".th-visual", [
		`width:${FIG_DESIGN_W.assistant}px`,
		"max-width:none",
		"min-width:0",
		...factor(
			FIG_DESIGN_W.assistant,
			`calc((100cqw - ${scaleLengths(ASSISTANT_GAP, LEN)}) * ${ASSISTANT_FR[0]} / ${ASSISTANT_SUM})`,
		),
		"transform-origin:0 0",
		`transform:${SCALED}`,
		comp(
			FIG_DESIGN_W.assistant,
			`calc((${FIG_DESIGN_W.assistant}px / ${FIG_RATIO[0] / FIG_RATIO[1]} + (${CAP_GAP_PX}px + ${CAP_H} * ${PX}) / var(${FIG_S_VAR})) * (var(${FIG_S_VAR}) - 1))`,
		),
		"will-change:auto",
	]),
	rule(".th-visual>.th-stage,.th-visual>.bw-frame", DESIGN),
	rule(".th-frame", [`height:calc(${designH(FIG_DESIGN_W.assistant)})`]),
	rule(".bw-frame", [`height:calc(${designH(FIG_DESIGN_W.assistant)})`]),
	rule(".bw-frame.on", [`width:calc((calc(${designH(FIG_DESIGN_W.assistant)}) - ${PDF_CHROME_PX}px) / ${PDF_PAGE_RATIO})`, `margin-left:${PDF_GAP_PX}px`]),
	rule("#cap-assistant", [
		`margin-top:calc(${CAP_GAP_PX}px / var(${FIG_S_VAR}))`,
		`font-size:calc(${BODY_FONT_PX} * ${PX} / var(${FIG_S_VAR}))`,
	]),
	rule("#cap-assistant .fg", [`font-size:calc(${CAP_FONT_PX} * ${PX} / var(${FIG_S_VAR}))`]),
];

// ── Famille N : crans tablette 941-1150 px (`hero.css`, `risques.css`) ramenés aux valeurs d'ordinateur, px bruts ──
// Parité gardée par Q20 ; `.ck-head` sans le terme `3vw` (convention H).

const NOTCH_NEUTRAL: readonly (readonly [selector: string, decls: readonly string[]])[] = [
	[".encart", ["padding:11px 13px 12px"]],
	[".encart .eh", ["font-size:12.5px", "margin-bottom:6px"]],
	[".erow", ["font-size:11.5px", "padding:4.5px 0"]],
	[".rrow,.thr", ["font-size:11.5px", "padding:3.5px 0"]],
	[".rk", ["font-size:11.5px"]],
	[".ck-grid", ["gap:10px"]],
	[".ck-head", ["padding:18px 20px 4px 20px"]],
	[".ck-head h3", ["font-size:24px"]],
	[".dtile .fic", ["width:34px", "height:34px"]],
	[".rq-page", ["padding:12px 14px 0"]],
	[".rq-grid", ["gap:9px", "padding-bottom:10px"]],
	[".rq-card", ["padding:9px 10px 8px", "gap:6px"]],
	[".rq-card h4", ["font-size:11px"]],
	[".rqb", ["font-size:8.5px", "padding:1.5px 6px"]],
	[".rqb svg", ["width:9px", "height:9px"]],
	[".rq-cbody", ["gap:7px"]],
	[".rq-info", ["font-size:8.5px", "line-height:1.45", "gap:10px"]],
	[".rq-src", ["font-size:7.5px", "padding-top:5px"]],
	[".rq-disc", ["padding:7px 2px 9px", "font-size:7.5px"]],
	[".rq-skel", ["padding:9px 10px"]],
	[".rq-side", ["font-size:10px"]],
	[".rqt-xs", ["font-size:7.5px"]],
	[".rqt-sm", ["font-size:8.5px"]],
	[".rqt-base", ["font-size:10px"]],
	[".rqt-g1", ["gap:2.5px"]],
	[".rqt-g15", ["gap:3.5px"]],
	[".rqt-g2", ["gap:5px"]],
	[".rqt-g3", ["gap:7.5px"]],
	[".rqt-g4", ["gap:10px"]],
	[".rqt-g6", ["gap:15px"]],
	[".rqt-gx5", ["column-gap:12.5px"]],
	[".rqt-gy2", ["row-gap:5px"]],
	[".rqt-mt1", ["margin-top:2.5px"]],
	[".rqt-mt2", ["margin-top:5px"]],
	[".rqt-pt3", ["padding-top:7.5px"]],
];

const N = NOTCH_NEUTRAL.map(([s, d]) => rule(s, d));

/** Feuille de composition : préambule `@property` puis bloc PAGE, insérée dans `authoredCss` avant `mobileCss`. */
export const compositionCss: string = `${FIG_PROPS}\n@media ${PAGE_QUERY}{${[V, ...I, ...H, B, X, ...F, ...N].join("\n")}}`;

/**
 * Jumelle d'un groupe de feuilles consécutives, à injecter dans un `<style>` sans `precedence` juste
 * après la dernière feuille du groupe ; appelée une fois par module (constante).
 *
 * @param sheets feuilles sources, dans l'ordre
 * @returns bloc `@media` PAGE contenant leurs jumelles
 * @throws Error `twinSheet : …` sur feuille mal formée
 */
export function cadreTwin(...sheets: readonly string[]): string {
	return `@media ${PAGE_QUERY}{${sheets
		.map((s) => twinSheet(s, TWIN_OPTIONS))
		.filter((t) => t !== "")
		.join("\n")}}`;
}
